/**
 * FASE 20.9 — TOOL RESULT SAFETY
 *
 * Strip all PII and internal metadata BEFORE injecting data into LLM prompts.
 *
 * INVARIANT: nothing in STRIP_FIELDS ever reaches the LLM context string.
 * Never pass raw Supabase rows to compactDataContext — always sanitize first.
 */

// Fields that must be removed from every row before LLM injection
const STRIP_FIELDS = new Set([
  "phone", "phone_number", "email", "email_address",
  "address", "street", "location", "coordinates", "lat", "lng",
  "notes", "private_notes", "internal_notes",
  "password", "token", "secret", "api_key",
  "created_by", "updated_by", "actor_profile_id", "profile_id",
  "raw_app_meta_data", "raw_user_meta_data",
  "sync_status", "_outbox_id", "_is_offline", "_local_id",
]);

// UUID-like fields to drop (except 'id' which is needed for correlation)
const STRIP_UUID_FIELDS = new Set([
  "district_id", "church_id", "family_id", "person_id",
  "target_family_id", "target_person_id",
  "coordinator_id", "pastor_id",
]);

/**
 * Sanitize a single row object, removing PII and metadata fields.
 * @param {Object} row
 * @returns {Object}
 */
export function sanitizeRow(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return row;

  const clean = {};
  for (const [key, value] of Object.entries(row)) {
    if (STRIP_FIELDS.has(key)) continue;
    if (STRIP_UUID_FIELDS.has(key)) continue;
    // Recursively sanitize nested objects (e.g. joined relations)
    if (value && typeof value === "object" && !Array.isArray(value)) {
      clean[key] = sanitizeRow(value);
    } else if (Array.isArray(value)) {
      clean[key] = value.map((v) =>
        v && typeof v === "object" ? sanitizeRow(v) : v
      );
    } else {
      clean[key] = value;
    }
  }
  return clean;
}

/**
 * Sanitize an array of rows.
 * @param {Object[]} rows
 * @returns {Object[]}
 */
export function sanitizeRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(sanitizeRow);
}

/**
 * Verify a sanitized object contains no obvious PII.
 * Returns array of field names that look risky (for dev logging only).
 * @param {Object} obj
 * @returns {string[]}
 */
export function auditForPII(obj, _path = "") {
  if (!obj || typeof obj !== "object") return [];
  const risks = [];
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = _path ? `${_path}.${key}` : key;
    if (STRIP_FIELDS.has(key) || STRIP_UUID_FIELDS.has(key)) {
      risks.push(fullKey);
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      risks.push(...auditForPII(value, fullKey));
    }
  }
  return risks;
}
